/**
 * variationStatusConfig.js
 *
 * Single source of truth for the "variation sample status" values used
 * throughout the opportunity / manage / review flows.
 *
 * Each entry carries:
 *  - key         : the raw value stored on the variation (e.g. `sampleStatus`)
 *  - label       : the human-readable short name shown in UI
 *  - color       : Semantic UI color name used for Labels / dots
 *  - icon        : Semantic UI icon name
 *  - description : one-line plain-English explanation used in tooltips /
 *                  legend panels so color-blind users and keyboard-only
 *                  users have a non-color affordance.
 *
 * Order here controls the order shown in the legend and dropdowns.
 */

export const VARIATION_STATUSES = [
  {
    key: 'waiting',
    label: 'Waiting',
    color: 'grey',
    icon: 'clock outline',
    description:
      'No recording submitted yet. The actor has not started this variation.'
  },
  {
    key: 'recorded',
    label: 'Recorded',
    color: 'green',
    icon: 'microphone',
    description:
      'The actor has recorded audio but has not yet submitted it for review.'
  },
  {
    key: 'submitted',
    label: 'Submitted',
    color: 'blue',
    icon: 'paper plane',
    description:
      'Submitted by the actor and awaiting your review.'
  },
  {
    key: 'approved',
    label: 'Approved',
    color: 'purple',
    icon: 'check circle',
    description:
      'You have approved this recording.'
  },
  {
    key: 'rejected',
    label: 'Rejected',
    color: 'red',
    icon: 'times circle',
    description:
      'You have rejected this recording; the actor must re-record.'
  }
];

/**
 * Look up a config entry by its key. Falls back to "waiting" (the
 * default for a variation with no recording yet) if the key is unknown
 * so nothing ever renders without color/icon.
 */
export const getVariationStatus = (key) =>
  VARIATION_STATUSES.find((s) => s.key === key) || VARIATION_STATUSES[0];

/**
 * Handy map from key -> config for O(1) lookups when iterating large
 * lists (e.g. filtering). Kept in sync with VARIATION_STATUSES.
 */
export const VARIATION_STATUS_MAP = VARIATION_STATUSES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});
